import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  Tooltip,
  ListSubheader,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  QrCode as QrCodeIcon,
  AccountCircle,
  Email as EmailIcon,
  Security as SecurityIcon,
  ViewColumn as ViewColumnIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon,
  Check as CheckIcon,
  NotificationsActive as NotificationsActiveIcon,
} from '@mui/icons-material';
import api from '../api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import { useThemeMode } from '../ThemeContext';
import OfflineIndicator from './OfflineIndicator';
import LanguageToggle from './LanguageToggle';
import NotificationBell from './NotificationBell';
import BottomNav from './BottomNav';
import { subscribeToPush, isPushSupported } from '../utils/pushNotifications';
import { updateAppBadge } from '../utils/badgeService';
import { useThemeColors } from '../hooks/useThemeColors';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

export default function Layout() {
  const [, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminToolsOpen, setAdminToolsOpen] = useState(false);
  const theme = useTheme();
  // isPhone: phones only (xs)
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole } = useAuth();
  const { t } = useTranslation();
  const { themePreference, setThemePreference } = useThemeMode();
  const { drawerBackground, pageBackground, isDark, accentBlue } = useThemeColors();

  // Check if a menu item is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Subscribe to push notifications on mount
  useEffect(() => {
    if (user && isPushSupported()) {
      // Delay subscription to avoid blocking initial render
      const timer = setTimeout(() => {
        subscribeToPush().catch(err => {
          console.log('[Push] Subscription not available:', err.message);
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Update app icon badge with open work order count
  useEffect(() => {
    if (user) {
      // Set badge on load (slight delay to not compete with initial API calls)
      const initialTimer = setTimeout(() => updateAppBadge(), 3000);
      // Refresh badge every 5 minutes
      const interval = setInterval(() => updateAppBadge(), 5 * 60 * 1000);
      return () => { clearTimeout(initialTimer); clearInterval(interval); };
    }
  }, [user]);

  const menuItems = [
    { text: t('layout.dashboard'), icon: <DashboardIcon />, path: '/', roles: ['operator', 'technician', 'supervisor', 'administrator', 'janitor', 'office_manager'] },
    { text: t('layout.assets'), icon: <InventoryIcon />, path: '/assets', roles: ['operator', 'technician', 'supervisor', 'administrator', 'office_manager'] },
    { text: t('layout.workOrders'), icon: <BuildIcon />, path: '/work-orders', roles: ['operator', 'technician', 'supervisor', 'administrator', 'janitor', 'office_manager'] },
    { text: t('layout.pmSchedules'), icon: <ScheduleIcon />, path: '/pm-schedules', roles: ['supervisor', 'administrator', 'office_manager'] },
    { text: t('layout.qrCodes'), icon: <QrCodeIcon />, path: '/qr-codes', roles: ['supervisor', 'administrator'] },
  ];

  const adminTools = [
    { text: t('layout.users'), icon: <PeopleIcon />, path: '/users' },
    { text: t('layout.columnSettings'), icon: <ViewColumnIcon />, path: '/column-settings' },
    { text: t('layout.rolePermissions'), icon: <SecurityIcon />, path: '/role-management' },
    { text: t('layout.emailSetup'), icon: <EmailIcon />, path: '/email-setup' },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
    if (isPhone) {
      setMobileOpen(false);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const handleThemeChange = (preference) => {
    setThemePreference(preference);
    handleProfileMenuClose();
  };

  const handleTestNotification = async () => {
    try {
      const response = await api.post('/push/test');
      alert(`Test notification sent! (${response.data.sent}/${response.data.total} devices)`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send test notification');
    }
    handleProfileMenuClose();
  };

  // Active menu item styles
  const getMenuItemStyles = (path) => ({
    borderLeft: isActive(path) ? `4px solid ${accentBlue}` : '4px solid transparent',
    backgroundColor: isActive(path)
      ? (isDark ? 'rgba(100, 181, 246, 0.15)' : 'rgba(30, 58, 95, 0.1)')
      : 'transparent',
    '&:hover': {
      backgroundColor: isDark ? 'rgba(100, 181, 246, 0.2)' : 'rgba(30, 58, 95, 0.15)',
    },
  });

  // Theme options
  const themeOptions = [
    { value: 'light', label: t('layout.themeLight'), icon: <LightModeIcon fontSize="small" /> },
    { value: 'dark', label: t('layout.themeDark'), icon: <DarkModeIcon fontSize="small" /> },
    { value: 'system', label: t('layout.themeSystem'), icon: <SystemModeIcon fontSize="small" /> },
  ];

  // Full drawer for desktop
  const drawer = (
    <Box>
      <Toolbar sx={{ backgroundColor: drawerBackground, display: 'flex', justifyContent: 'center', py: 2 }}>
        <img
          src={isDark ? "/sleeptronic-logo-white.png" : "/sleeptronic-logo.png"}
          alt="Sleeptronic Logo"
          style={{ maxWidth: '180px', height: 'auto' }}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems
          .filter((item) => hasRole(...item.roles))
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                sx={getMenuItemStyles(item.path)}
              >
                <ListItemIcon sx={{ color: isActive(item.path) ? accentBlue : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                    color: isActive(item.path) ? accentBlue : 'inherit',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

        {/* Administrator Tools - Collapsible */}
        {hasRole('administrator') && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setAdminToolsOpen(!adminToolsOpen)}>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText primary={t('layout.administratorTools')} />
                {adminToolsOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminToolsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {adminTools.map((tool) => (
                  <ListItem key={tool.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4, ...getMenuItemStyles(tool.path) }}
                      onClick={() => handleMenuClick(tool.path)}
                    >
                      <ListItemIcon sx={{ color: isActive(tool.path) ? accentBlue : 'inherit' }}>
                        {tool.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={tool.text}
                        primaryTypographyProps={{
                          fontWeight: isActive(tool.path) ? 600 : 400,
                          color: isActive(tool.path) ? accentBlue : 'inherit',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );

  // Collapsed drawer for tablets (icons only)
  const collapsedDrawer = (
    <Box>
      <Toolbar sx={{ backgroundColor: drawerBackground, display: 'flex', justifyContent: 'center', py: 2, minHeight: 80 }}>
        <img
          src="/icon-192.png"
          alt="Sleeptronic"
          style={{ width: 40, height: 40 }}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems
          .filter((item) => hasRole(...item.roles))
          .map((item) => (
            <Tooltip title={item.text} placement="right" key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleMenuClick(item.path)}
                  sx={{
                    justifyContent: 'center',
                    px: 2,
                    py: 1.5,
                    borderLeft: isActive(item.path) ? `4px solid ${accentBlue}` : '4px solid transparent',
                    backgroundColor: isActive(item.path)
                      ? (isDark ? 'rgba(100, 181, 246, 0.15)' : 'rgba(30, 58, 95, 0.1)')
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(100, 181, 246, 0.2)' : 'rgba(30, 58, 95, 0.15)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: isActive(item.path) ? accentBlue : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </ListItem>
            </Tooltip>
          ))}

        {/* Admin tools for collapsed mode */}
        {hasRole('administrator') && adminTools.map((tool) => (
          <Tooltip title={tool.text} placement="right" key={tool.text}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleMenuClick(tool.path)}
                sx={{
                  justifyContent: 'center',
                  px: 2,
                  py: 1.5,
                  borderLeft: isActive(tool.path) ? `4px solid ${accentBlue}` : '4px solid transparent',
                  backgroundColor: isActive(tool.path)
                    ? (isDark ? 'rgba(100, 181, 246, 0.15)' : 'rgba(30, 58, 95, 0.1)')
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(100, 181, 246, 0.2)' : 'rgba(30, 58, 95, 0.15)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: isActive(tool.path) ? accentBlue : 'inherit' }}>
                  {tool.icon}
                </ListItemIcon>
              </ListItemButton>
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <OfflineIndicator />
      <AppBar
        position="fixed"
        sx={{
          // On phones, full width. On tablet/desktop, account for sidebar
          width: { xs: '100%', sm: `calc(100% - ${collapsedDrawerWidth}px)`, md: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, sm: `${collapsedDrawerWidth}px`, md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          {/* No hamburger menu on phones - we use bottom nav instead */}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {t('layout.maintenanceSystem')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageToggle />
            <NotificationBell />
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>{user?.full_name}</Typography>
            <IconButton color="inherit" onClick={handleProfileMenuOpen}>
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="textSecondary">
            {user?.role?.toUpperCase()}
          </Typography>
        </MenuItem>
        <Divider />

        {/* Theme Selection */}
        <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
          {t('layout.theme')}
        </ListSubheader>
        {themeOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleThemeChange(option.value)}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {option.icon}
            </ListItemIcon>
            <ListItemText primary={option.label} />
            {themePreference === option.value && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: accentBlue }} />
            )}
          </MenuItem>
        ))}

        <Divider />
        <MenuItem onClick={handleTestNotification}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <NotificationsActiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Test Notification" />
        </MenuItem>
        <MenuItem onClick={handleLogout}>{t('layout.logout')}</MenuItem>
      </Menu>

      {/* Sidebar - hidden on phones, shown on tablet/desktop */}
      <Box
        component="nav"
        sx={{
          width: { xs: 0, sm: collapsedDrawerWidth, md: drawerWidth },
          flexShrink: { sm: 0 },
          display: { xs: 'none', sm: 'block' }
        }}
      >
        {/* Tablet drawer (collapsed - icons only) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: collapsedDrawerWidth },
          }}
          open
        >
          {collapsedDrawer}
        </Drawer>

        {/* Desktop drawer (full) */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // Extra bottom padding on phones for bottom nav (increased for larger nav bar)
          pb: isPhone ? 14 : 3,
          width: { xs: '100%', sm: `calc(100% - ${collapsedDrawerWidth}px)`, md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: pageBackground,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Bottom Navigation (phones only) */}
      {isPhone && <BottomNav />}
    </Box>
  );
}
